import dayjs from 'dayjs';
import { useStoreState } from 'easy-peasy';
import { PropTypes } from 'prop-types';
import React, { useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { ScrollingContainer } from '@ge/components/scrolling-container';
import { DateTimeFormats, DefaultTime } from '@ge/models/constants';
import { TaskContext } from '@ge/shared/components/tasks/task-context';
import { addUTCOffset } from '@ge/shared/util/time-date';

import {
  ColumnTitle,
  ColumnDetail,
  DisplayField,
  DisplayLabel,
  MultiRow,
  ColumnSubtitle,
} from '../../../task-template-shared';
const ScrollableTable = styled.div`
  &.scrolling-container-wrapper {
    display: flex;
    height: 162px;
  }
  .simplebar-track.simplebar-vertical {
    width: 4px;
  }
`;
export const ViewSchedule = ({ task }) => {
  const { t } = useTranslation(['tasks'], { useSuspense: false });
  const { crewsSg } = useStoreState((state) => state.workers);
  const scheduleDate = dayjs(task?.scheduleDateTz).format(DateTimeFormats.ENDPOINT_PARAM);
  const {
    taskState: { timezone },
  } = useContext(TaskContext);
  const [scheduledMultiDay, setScheduledMultiDay] = useState([]);
  const generateScheduleArray = (schedule, arr) => {
    const startDate = new Date(schedule?.scheduleStartDate);
    const endDate = new Date(schedule?.scheduleEndDate);
    const dailyStartTime = dayjs.utc(schedule?.dailyStartTime).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
    for (let d = startDate; d <= endDate; d.setDate(d.getDate() + 1)) {
      // const date = new Date(d).toISOString();
      const date = dayjs(d).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
      arr.push({
        date: date,
        startTime: dailyStartTime,
        estimatedDailyDurationHours: schedule?.estimatedDailyDurationHours,
        estimatedDailyDurationMinutes: schedule?.estimatedDailyDurationMinutes,
        crewIds: schedule?.crewIds,
      });
    }
  };

  const updatedCrewListForSingleDay = crewsSg
    ?.map(
      (crew) =>
        scheduleDate &&
        dayjs(scheduleDate).isBetween(
          dayjs(crew.crewStartDate),
          dayjs(crew.crewEndDate),
          null,
          '[]',
        ) && {
          value: crew?._id,
          label: crew?.name,
        },
    )
    .filter((crew) => crew);
  const updatedCrewList = crewsSg
    ?.map((crew) => {
      return {
        value: crew?._id,
        label: crew?.name,
      };
    })
    .filter((crew) => crew);

  const getCrewName = (crewId) => {
    if (crewId === 'Unassigned') return 'Unassigned';
    return crewId ? updatedCrewList?.map((crew) => crew?.value === crewId && crew?.label) : '';
  };

  const checkTimeformat = (str) => {
    if (str == undefined) return '';
    else if (typeof str == 'string') return Number(str) <= 9 ? '0' + Number(str) : str;
    else return str <= 9 ? '0' + str : str;
  };

  const totalDaysInBetween = (arr) => {
    const startDate = new Date(arr[0]?.date);
    const endDate = new Date(arr[arr.length - 1]?.date);
    const diffInMs = endDate - startDate;
    const totalDays = diffInMs / (1000 * 60 * 60 * 24) + 1; // Convert milliseconds to days
    return Math.round(totalDays);
  };

  const formatDate = (dateString) => {
    if (dateString?.includes(' ')) {
      // means the date is in "09 Oct 24" format, below logic to convert
      const [day, month, year] = dateString.split(' ');
      const monthMap = {
        Jan: '01',
        Feb: '02',
        Mar: '03',
        Apr: '04',
        May: '05',
        Jun: '06',
        Jul: '07',
        Aug: '08',
        Sep: '09',
        Oct: '10',
        Nov: '11',
        Dec: '12',
      };
      return `20${year}-${monthMap[month]}-${day.padStart(2, '0')}`;
    }

    // If the date is already in ISO format, return it directly
    return dateString;
  };

  const sortDatesInAsc = (a, b) => {
    let timeA = a.startTime;
    let timeB = b.startTime;
    let dateA = a.date;
    let dateB = b.date;
    if (a?.startTime?.length > 6 && b?.startTime?.length > 6) {
      timeA = a.startTime?.substring(11, 19);
      timeB = b.startTime?.substring(11, 19);
    }
    if (a?.date?.length > 10 && b?.date?.length > 10) {
      dateA = a.date?.substring(0, 10);
      dateB = b.date?.substring(0, 10);
    } else {
      dateA = formatDate(a.date);
      dateB = formatDate(b.date);
    }
    const formattedDateA = new Date(`${dateA}T${timeA}Z`);
    const formattedDateB = new Date(`${dateB}T${timeB}Z`);

    // Compare the two dates
    return formattedDateA - formattedDateB;
  };

  const displayDatePart = (datePart) => {
    if (datePart && datePart == t('form.unscheduled', 'Unscheduled')) {
      return datePart;
    } else if (datePart && typeof datePart == 'string' && datePart.includes(' ')) {
      return datePart;
    } else if (datePart && typeof datePart == 'string') {
      return dayjs(datePart).format(DateTimeFormats.DEFAULT_DATE);
    } else {
      return DefaultTime.DEFAULT_DATE;
    }
  };

  const isAmPm = new Intl.DateTimeFormat(navigator.language, {
    hour: 'numeric',
  }).resolvedOptions().hour12;

  const displayTimePart = (timePart) => {
    const format = isAmPm ? 'hh:mm A' : DateTimeFormats.DEFAULT_TIME;
    if (
      timePart?.date &&
      timePart?.date.includes(' ') &&
      timePart?.startTime &&
      timePart?.startTime.length > 5 &&
      timePart?.estimatedDailyDurationHours
    ) {
      // If `isAmPm` is true, format in 12-hour (AM/PM) format, otherwise use default 24-hour format
      return addUTCOffset(timePart?.startTime).format(format);
    } else if (timePart?.startTime && timePart?.startTime.length < 6) {
      if (isAmPm) {
        return task?.startTime
          ? dayjs(task?.scheduleDate)
              .set('hour', task.startTime.split(':')[0])
              .set('minute', task.startTime.split(':')[1])
              .format('hh:mm A')
          : null;
      }
      return timePart?.startTime;
    } else if (timePart?.startTime && timePart?.startTime.length > 6) {
      return addUTCOffset(timePart?.startTime).format(format);
    } else {
      return DefaultTime.TASK_DEFAULT_START_TIME;
    }
  };
  const displayDurationPart = (datePart) => {
    if (
      datePart?.estimatedDailyDurationHours == undefined ||
      datePart?.estimatedDailyDurationMinutes == undefined
    ) {
      return '';
    } else {
      return datePart?.estimatedDailyDurationHours !== '' &&
        datePart?.estimatedDailyDurationMinutes !== ''
        ? checkTimeformat(datePart?.estimatedDailyDurationHours) +
            ' hrs ' +
            checkTimeformat(datePart?.estimatedDailyDurationMinutes) +
            ' mins'
        : '';
    }
  };

  const formatIndDaysArray = (obj) => {
    let updatedObj = obj;
    updatedObj['date'] = dayjs(updatedObj['date']).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
    return updatedObj;
  };

  useEffect(() => {
    let multiDayArray = [];
    if (Object.keys(task?.multiDaySchedule || {}).length === 0) {
      let singleDayObj = {
        date: task.scheduleDateTz
          ? task.scheduleDateTz.format(DateTimeFormats.DEFAULT_DATE)
          : t('form.unscheduled', 'Unscheduled'),
        startTime: task?.startTime ? task.startTime : DefaultTime.TASK_DEFAULT_START_TIME,
        crewIds: task?.crewIds?.length ? task?.crewIds[0] : '',
      };
      multiDayArray.push(singleDayObj);
    } else {
      task?.multiDaySchedule?.isMultiDay &&
        task?.multiDaySchedule?.schedules.forEach((obj) => {
          if (obj?.individualDays) {
            obj?.individualDays.forEach((x) => multiDayArray.push(formatIndDaysArray(x)));
          } else {
            if (obj?.scheduleStartDate && obj.scheduleEndDate)
              generateScheduleArray(obj, multiDayArray);
          }
        });
      multiDayArray?.sort(sortDatesInAsc);
    }
    setScheduledMultiDay(multiDayArray);
  }, [task]);

  return (
    <div className="task-panel-row">
      <ColumnTitle>
        <h4>{t('section_header.schedule', 'Schedule')}</h4>
        <ColumnSubtitle>
          Total Days:{' '}
          {scheduledMultiDay?.length > 1
            ? checkTimeformat(totalDaysInBetween(scheduledMultiDay))
            : scheduledMultiDay[0]?.date !== 'Unscheduled'
            ? checkTimeformat(scheduledMultiDay?.length)
            : 0}
        </ColumnSubtitle>
      </ColumnTitle>
      <ColumnDetail className="scheduletiming">
        <MultiRow>
          <DisplayLabel>{t('form.scheduled_start_date', 'Scheduled Start Date')}</DisplayLabel>
          <DisplayLabel>{t('form.daily_start_time', 'Daily Start Time')}</DisplayLabel>
          {scheduledMultiDay.length > 1 && (
            <DisplayLabel>{t('form.est_daily_duration', 'Est. Daily Duration')}</DisplayLabel>
          )}
          <DisplayLabel>{t('form.individual_assignee', 'Assignee')}</DisplayLabel>
        </MultiRow>
        <ScrollableTable className="scrolling-container-wrapper">
          <ScrollingContainer autoHide={true}>
            {scheduledMultiDay?.map((datePart, idx) => (
              <MultiRow key={idx}>
                <DisplayField className="wordbreak">{displayDatePart(datePart.date)}</DisplayField>
                <DisplayField className="wordbreak">{displayTimePart(datePart)}</DisplayField>
                {scheduledMultiDay.length > 1 && (
                  <DisplayField>{displayDurationPart(datePart)}</DisplayField>
                )}
                <DisplayField className="crewName">
                  {getCrewName(datePart?.crewIds || datePart?.crewId || 'Unassigned')}
                </DisplayField>
              </MultiRow>
            ))}
          </ScrollingContainer>
        </ScrollableTable>
      </ColumnDetail>
    </div>
  );
};

ViewSchedule.propTypes = {
  task: PropTypes.object.isRequired,
};
